import Vue from 'vue'
import auth0 from 'auth0-js'
// Helpers
import {
  storeUserInLocalStorage,
} from '../helpers'
import {
  Tenant,
} from '../api'
import {
  CGroupEnd,
  CIGroup,
  CLog,
} from '../consoleMixin'
import {
  captureSentryEvent,
  initializeSentry,
} from '../sentryMixin'
import {
  LaunchDarkly,
} from '../launchDarkly'
import FactoringAPI from '../FactoringAPI'

/*
 * login via universal login (webAuth.authorize) + redirect back to target
 * Login via email/password (webAuth.login) + redirect back to target
 * Get JWT Token (store in plugin state)
 * Logout
 * Auth state:
 *   * User (make request to our BE with JWT token
 *   * isAuthenticated (when JWT)
 *   * Loading (manual)
 *   * JWT
 * Get instance
 */

let instance

/** Returns the current instance of the SDK */
export const getInstance = () => {
  return instance
}

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = () => {
  if (instance) {
    return instance
  }

  // The 'instance' is simply a Vue object
  instance = new Vue({
    /** Use this lifecycle method to instantiate the SDK client */
    async created () {
      // We want to initialize sentry so we can log authentication errors from the get go
      // and Sentry does not require an auth token
      await initializeSentry()

      CIGroup('%cApp Initialization (Auth0, LD, Sentry, etc.)', 'color: orange')

      this.webAuth = await new auth0.WebAuth({
        audience: process.env.VUE_APP_AUTH0_AUDIENCE,
        clientID: process.env.VUE_APP_AUTH0_CLIENT_ID,
        domain: process.env.VUE_APP_AUTH0_DOMAIN,
        // Both are required by Auth0, IDK why
        redirect_uri: `${window.location.origin}/auth-0-callback`,
        redirectUri: `${window.location.origin}/auth-0-callback`,
        responseType: 'token id_token',
        scope: 'openid profile email',
      })

      try {
        if (window.Cypress) {
          const auth0Cypress = JSON.parse(localStorage.getItem('auth0Cypress'))

          if (auth0Cypress) {
            await this.setSession({
              accessToken: auth0Cypress.body.access_token,
              expiresIn: auth0Cypress.body.expires_in,
              idToken: auth0Cypress.body.id_token,
              idTokenPayload: auth0Cypress.body.decodedToken.user,
            })

            this.onRedirectCallback('/invoices/pending')
          }
          else {
            console.log('auth0Cypress is null in LS')
          }
        }
        else if (window.location.hash.length) {
          CLog('Redirected from Auth0; URL contains [hash]')
          // handleAuthentication and capture a redirectUrl if present in appState
          const { appState } = await this.handleAuthentication()

          this.redirectRoute = appState?.targetUrl || null

          this.onRedirectCallback(this.redirectRoute)
        }
        else {
          const session = await this.checkSession()
          if (session) {
            await this.setSession(session)
          }
        }
      }
      catch (error) {
        this.error = error
        CLog('%cauthService: created() try...catch ERROR', 'color: orange', error)
      }
      finally {
        this.scheduleTokenRenewal()
        this.loading = false
      }

      CGroupEnd()
    },

    data () {
      return {
        accessToken: null,
        error: null,
        expiresAt: null,
        idToken: null,
        loading: true,
        profile: null,
        redirectRoute: null,
        user: null,
        webAuth: null,
      }
    },

    methods: {
      checkSession () {
        CLog('%cauthService: Checking Session', 'color: orange')
        return new Promise((resolve, reject) => {
          this.webAuth.checkSession({}, (err, authResult) => {
            if (err) {
              return reject(err)
            }

            CLog('%cauthService: session', 'color: orange', authResult)
            resolve(authResult)
          })
        })
      },

      getAccessToken () {
        return this.accessToken
      },

      // Called after the user is redirected from Auth0.  This method reads the hash segment
      // of the URL to fetch the user details and the id token.
      async handleAuthentication () {
        CLog('%cauthService: Authenticating', 'color: orange')
        this.loading = true

        return new Promise((resolve, reject) => {
          this.webAuth.parseHash((error, authResult) => {
            if (error || (!authResult || !authResult.idToken)) {
              this.error = error

              if ([ 'access_denied', 'invalid_token', 'login_required' ].includes(error.error)) {
                // TODO: update with appState
                Vue.prototype.$Toastr.error(error.errorDescription, `Authentication Error: ${error.error}`)
                // Allow the error to display for 3 seconds and then redirect and logout
                setTimeout(this.logout, '3000')
              }

              // Log in production for debugging
              console.error('%cauthService: handleRedirectCallback ERROR', 'color: orange', error.error)
              captureSentryEvent(
                'Auth0Plugin "handleRedirectCallback" in created()',
                {
                  details: error,
                  isAuthenticated: this.isAuthenticated(),
                  location: window.location,
                  user: this.user,
                }
              )

              return reject(error)
            }
            resolve(authResult)
          })
        }).then(async (authResult) => {
          await this.setSession(authResult)
          this.loading = false
          return authResult
        })
      },

      isAuthenticated () {
        CLog('%cChecking Auth Status', 'color: orange')
        return new Date().getTime() < this.expiresAt
      },

      loginWithRedirect (options) {
        CLog('%cauthService: loginWithRedirect', 'color: orange', options?.appState?.targetUrl)
        this.webAuth.authorize(options)
      },

      // sets profile, id_token, expiresAt to null
      async logout () {
        CLog('%cauthService: logout', 'color: orange')
        await LaunchDarkly.close()
        localStorage.removeItem('user', null)
        localStorage.removeItem('master', null)
        localStorage.removeItem('controlledClient', null)
        clearTimeout(this.tokenRenewalTimeout)
        return await this.webAuth.logout({
          clientID: process.env.VUE_APP_AUTH0_CLIENT_ID,
          returnTo: `${process.env.VUE_APP_APP_URL}/invoices/pending`,
        })
      },

      onRedirectCallback (url) {
        CLog('%Handling Redirect', 'color:pink', url)
        window.history.replaceState({}, '', url
          ? url
          : window.location.pathname
        )
      },

      // Used to update this.$auth.user throughout the application
      async refreshAuthUserInfo () {
        try {
          await storeUserInLocalStorage()
          this.user = JSON.parse(localStorage.getItem('user'))
        }
        catch (error) {
          this.error = error
          // Log in production for debugging
          console.error('%cauthService: refreshAuthUserInfo ERROR', 'color: orange', error)
          captureSentryEvent(
            'Auth0Plugin "refreshAuthUserInfo"',
            {
              details: error,
              isAuthenticated: this.isAuthenticated(),
              response: error.response,
              user: this.user,
            }
          )
        }
      },

      // check session and sets the auth tokens in memory on the Auth instance
      async renewTokens () {
        return this
          .checkSession()
          .then((authResult) => {
            this.setSession(authResult)
          })
      },

      scheduleTokenRenewal () {
        const delay = this.expiresAt - Date.now()
        if (delay > 0) {
          this.tokenRenewalTimeout = setTimeout(() => {
            this.renewTokens()
          }, delay)
        }
      },

      async setSession (authResult) {
        this.accessToken = authResult.accessToken
        // set the time that the id token will expire at
        this.expiresAt = authResult.expiresIn * 1000 + new Date().getTime()
        this.idToken = authResult.idToken
        this.profile = authResult.idTokenPayload
        this.scheduleTokenRenewal()

        // Prevent calling /me during maintenance mode on /maintenance
        // This allows master employees to get to /admin, but the
        // router will block all other employees and send them
        // back to /maintenance
        const maintenanceOn = (await new FactoringAPI().getMaintenanceMode()).isEnabled
        if (maintenanceOn && window.location.pathname === '/maintenance') {
          return
        }

        try {
          await storeUserInLocalStorage()
          this.user = JSON.parse(localStorage.getItem('user'))
          await LaunchDarkly.init(this.user)

          this.error = null

          // getInstance() always returns undefined in Cypress
          if (window.Cypress) {
            const data = { ...this.$data }
            delete data.webAuth // webAuth creates a circular structure
            window.localStorage.setItem('auth0PluginDataForCypress', JSON.stringify(data))
          }

          return authResult
        }
        catch (error) {
          this.error = error
          // Log in production for debugging
          console.error('%cauthService: setSession ERROR', 'color: orange', error)
          const message = error.message === 'User is not in database'
            ? error.message
            : 'There was an error setting your user session. Please contact your account manager'
          Vue.prototype.$Toastr.error(message, 'Authentication Error')

          captureSentryEvent(
            'Auth0Plugin "setSession"',
            {
              details: error,
              isAuthenticated: this.isAuthenticated(),
              response: error.response,
              user: this.user,
            }
          )
        }
      },
    },
  })

  return instance
}

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install (Vue) {
    Vue.prototype.$auth = useAuth0()
  },
}
